import { ClearOutlined, EditOutlined, FilterOutlined } from '@ant-design/icons'
import { Button, Spin, Table, Tooltip } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { useGeneral, useUser } from '@contexts'
import { Carrier, RoleEnum } from '@types'

import { useFiltersV2 } from '../../contexts/FiltersContextV2'
import { UpdateOrderNumberModal } from '../../features/AdminRouter/UpdateOrderNumber'
import useContractsFilters from '../../hooks/useContracts/filters'
import useCarriersFilters from '../../hooks/useCarriers/filters'
import useGetOrders from '../../hooks/useGetOrders'
import arrayToOptions from '../../utils/array-to-options'
import {
  ContentContainer,
  StyledButton,
  StyledInput,
  StyledRow,
  StyledSelect,
} from '../StyledComponents'

const OrdersDashboard = () => {
  const { filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()
  const { filters, setFilters, handleResetFilters } = useFiltersV2()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { state: user } = useUser()
  const roles = user?.roles.map(role => role.name)
  const isAdmin = roles?.includes(RoleEnum.ADMIN)
  const { orders, ordersIsLoading } = useGetOrders(filters)
  const { contractsFilters, contractsFiltersIsLoading } = useContractsFilters(filters)
  const { carriersFilters, carriersFiltersIsLoading } = useCarriersFilters()

  useEffect(() => {
    console.log('orders', orders)
  }, [orders])

  const [selectedRecord, setSelectedRecord] = useState<any>(null)

  const clearAllFilters = useCallback(() => {
    handleResetFilters()
  }, [handleResetFilters])

  const handleContractFilterChange = useCallback(
    (value: any) => {
      setFilters((prevState: any) => ({
        ...prevState,
        page: 1,
        contract: value as string,
      }))
    },
    [setFilters],
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  const handleEditClick = (record: any) => {
    setSelectedRecord(record)
  }

  const closeUpdateOrderModal = () => {
    setSelectedRecord(null)
  }

  const actionButtons = (record: any) => (
    <div style={{ textAlign: 'center' }}>
      <Tooltip title={t('order_modal.edit_order_tooltip')} placement="top">
        <EditOutlined
          style={{ fontSize: '16px', color: '#1890ff', cursor: 'pointer' }}
          onClick={() => handleEditClick(record)}
        />
      </Tooltip>
    </div>
  )

  const handleCarrierFilterChange = (value: any) => {
    setFilters((prevState: any) => ({
      ...prevState,
      page: 1,
      carrierId: value as number[],
    }))
  }

  const handleOrderNumberFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setFilters((prevState: any) => {
        const newFilters = { ...prevState, page: 1 }
        if (value) {
          newFilters.orderNumber = parseInt(value, 10)
        } else {
          const { orderNumber, ...rest } = newFilters
          return rest
        }
        return newFilters
      })
    },
    [setFilters],
  )

  const carriersToOptions = (carriers: Carrier[] | undefined) => {
    return (
      carriers?.map(carrier => ({
        label: carrier.businessName,
        value: carrier.id,
      })) || []
    )
  }

  const columns = [
    {
      title: t('contract_dashboard.carrier'),
      key: 'businessName',
      dataIndex: 'carrier',
      ellipsis: true,
      render: (carrier: Carrier) => carrier && <>{carrier?.businessName}</>,
    },
    {
      title: t('contract_dashboard.contract'),
      dataIndex: 'contract',
      key: 'contract',
      ellipsis: true,
      render: (contract: string) => (
        <Link
          to={`${location.pathname}?contractNumber=${contract}`}
          style={{ display: 'block', color: '#1890ff' }}
          onClick={e => {
            e.preventDefault()
            handleContractFilterChange(contract)
            navigate(`${location.pathname}?contractNumber=${contract}`)
          }}
        >
          {contract}
        </Link>
      ),
    },
    {
      title: t('order_dashboard.order'),
      key: 'orderNumber',
      dataIndex: 'orderNumber',
      ellipsis: true,
      align: 'center' as const,
      render: (orderNumber: number | null) => (orderNumber ? orderNumber.toString() : '-'),
    },
    // {
    //   title: t('order_dashboard.date_validity_agreemen_from'),
    //   dataIndex: 'dateValidityAgreementFrom',
    //   key: 'dateValidityAgreementFrom',
    //   ellipsis: true,
    //   render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD') : '-'),
    // },
    // {
    //   title: t('order_dashboard.date_validity_agreemen_to'),
    //   dataIndex: 'dateValidityAgreementTo',
    //   key: 'dateValidityAgreementTo',
    //   ellipsis: true,
    //   render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD') : '-'),
    // },
    ...(isAdmin
      ? [
          {
            title: 'Acciones',
            key: 'acciones',
            ellipsis: true,
            align: 'center' as const,
            className: 'column-width',
            render: actionButtons,
          },
        ]
      : []),
  ]

  useEffect(() => {
    const contractNumber = searchParams.get('contractNumber')
    if (contractNumber && filters.contract !== contractNumber) {
      handleContractFilterChange(contractNumber)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, handleContractFilterChange])

  useEffect(() => {
    const service = searchParams.get('service')
    if (service && filters.asnum !== service) {
      setFilters((prevState: any) => ({
        ...prevState,
        page: 1,
        asnum: service,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setFilters])

  return (
    <ContentContainer className="admin-dashboard">
      <StyledRow className="sticky-options border-botom">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <Tooltip placement="bottomRight" title={t('globals.generics.clean_filters')}>
              <StyledButton
                className="inline option"
                onClick={clearAllFilters}
                icon={<ClearOutlined style={{ fontSize: '18px' }} />}
              />
            </Tooltip>
            <StyledSelect
              maxTagCount="responsive"
              showSearch
              // mode="multiple"
              allowClear
              placeholder={t('admin_router.filter_options.by_carrier.title')}
              options={carriersToOptions(carriersFilters)}
              filterOption={handleFilterOptions}
              value={filters?.carrierId}
              onChange={handleCarrierFilterChange}
              loading={carriersFiltersIsLoading}
            />
            <StyledSelect
              maxTagCount="responsive"
              showSearch
              allowClear
              placeholder={t('admin_router.filter_options.contract_filters.contract')}
              options={contractsFilters?.map(arrayToOptions())}
              filterOption={handleFilterOptions}
              suffixIcon={null}
              value={filters?.contract}
              onChange={handleContractFilterChange}
              loading={contractsFiltersIsLoading}
            />
            <StyledInput
              placeholder={t('admin_router.filter_options.order')}
              value={filters?.orderNumber?.toString() || ''}
              onChange={handleOrderNumberFilterChange}
              style={{
                borderColor: filters?.orderNumber ? '#1890ff' : undefined,
                backgroundColor: filters?.orderNumber ? '#f0f8ff' : undefined,
              }}
            />
          </div>
        </div>
      </StyledRow>
      <Spin spinning={ordersIsLoading}>
        <StyledRow className="data-table-container">
          <div style={{ width: '100%' }}>
            <style>
              {`
                .dataTable .ant-table-thead > tr > th,
                .dataTable .ant-table-tbody > tr > td {
                  width: ${isAdmin ? '16.66%' : '20%'} !important;
                  text-align: center !important;
                }
              `}
            </style>
            <Table
              dataSource={orders?.carrierContractsOrders?.filter(order => order.disabled !== true)}
              className="dataTable"
              columns={columns}
              pagination={{
                total: orders?.total,
                pageSize: filters?.limit as number,
                current: filters?.page as number,
                position: ['bottomCenter'],
                size: 'small',
                showSizeChanger: false,
                showQuickJumper: false,
                onChange: (page, pageSize) => {
                  setFilters(prev => ({ ...prev, page, limit: pageSize }))
                },
              }}
              showSorterTooltip={false}
              rowKey="id"
              scroll={{ x: true }}
              loading={ordersIsLoading}
              style={{ width: '100%', height: '100%' }}
              tableLayout="fixed"
            />
          </div>
        </StyledRow>
      </Spin>

      <UpdateOrderNumberModal
        isVisible={!!selectedRecord}
        onCancel={closeUpdateOrderModal}
        onSuccess={closeUpdateOrderModal}
        selectedRecord={selectedRecord}
      />
    </ContentContainer>
  )
}

export { OrdersDashboard }
